import {
  MemberAttributes,
  TeamMemberRole,
  TeamMemberRoleAttributes,
  TeamRole,
} from '@graphql/generated';

type SelectedMember = {
  id: string;
  email?: string | null;
  role?: string | null;
};

const toTeamRole = (role?: string | null): TeamRole =>
  Object.values(TeamRole).find((r) => r === role) ?? TeamRole.Member;

const toTeamMemberRole = (role: TeamRole): TeamMemberRole =>
  Object.values(TeamMemberRole).find((r) => r === (role as string)) ??
  TeamMemberRole.Member;

/**
 * Splits users picked in an add-members flow by whether they are already on
 * the team (TA-11187).
 *
 * `updateTeamMembers` only changes the role of existing members; anyone else
 * must go through `createTeamMemberInvitation`, and the backend bills their
 * seat at the moment they actually join. Invitations are keyed by email, so a
 * new user without one is dropped.
 */
export const splitTeamMemberChanges = (
  selected: readonly SelectedMember[],
  existingMemberIds: readonly string[]
): {
  roleUpdates: MemberAttributes[];
  invitations: TeamMemberRoleAttributes[];
} => {
  const existing = new Set(existingMemberIds);
  const roleUpdates: MemberAttributes[] = [];
  const invitations: TeamMemberRoleAttributes[] = [];

  selected.forEach((user) => {
    const role = toTeamRole(user.role);
    if (existing.has(user.id)) {
      roleUpdates.push({ userId: user.id, role });
      return;
    }

    const email = user.email?.trim();
    if (email) invitations.push({ email, role: toTeamMemberRole(role) });
  });

  return { roleUpdates, invitations };
};
